import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

/**
 * Drop this browser's push subscription (ticket 12, spec §E).
 *
 * A module-level function rather than a hook or a context value, because
 * `AuthProvider` encloses `PushProvider` (`App.tsx`) — so `AuthContext` cannot
 * call `usePush()`. Both `AuthContext.logout` and `PushContext.unsubscribe`
 * call this, so there is one implementation of "the subscription belongs to the
 * principal, and the browser drops it when the principal goes away".
 *
 * Never throws and never blocks for long: logout ends in
 * `window.location.href`, and a service worker that is slow or wedged must not
 * be able to hold a logout open. Whatever fails here reaps itself server-side —
 * the next notification to a browser-side-unsubscribed endpoint returns 410 and
 * `sendToSubscriptions` already deletes on 410.
 */
export const DEFAULT_PUSH_DROP_TIMEOUT_MS = 1500;

/**
 * `done: false` means the work did not finish — the timeout won, or something
 * threw. Callers that keep the browser subscription need this: a release that
 * silently did not happen leaves the device bound to the principal it is
 * leaving (code review r2, finding 1).
 */
export interface PushDropResult {
  done: boolean;
}

export async function dropPushSubscription(opts: {
  /** False on the 401 path: the server call would 401 too. */
  notifyServer: boolean;
  /**
   * Keep the browser's subscription and release only the server-side binding.
   *
   * For a principal that is *changing* rather than going away — Smart-ID
   * `/switch-role`, which clears the entity while the person stays logged in
   * (code review r1, finding 1). The row must go, because it names the entity
   * being left; the browser subscription must stay, because the sync effect
   * re-POSTs it under the new principal and would not create a fresh one on its
   * own. It must run *before* the transition, while the caller still owns the
   * row: `/api/push/unsubscribe` needs a principal, and the role picker has none.
   */
  keepBrowserSubscription?: boolean;
  timeoutMs?: number;
}): Promise<PushDropResult> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_PUSH_DROP_TIMEOUT_MS;

  const work = async (): Promise<void> => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    const { endpoint } = subscription;
    if (!opts.keepBrowserSubscription) {
      await subscription.unsubscribe();
    }

    if (opts.notifyServer) {
      await axios.post(
        `${API_URL}/api/push/unsubscribe`,
        { endpoint },
        { withCredentials: true }
      );
    }
  };

  try {
    return await Promise.race([
      work().then(() => ({ done: true })),
      new Promise<PushDropResult>((resolve) =>
        setTimeout(() => resolve({ done: false }), timeoutMs)
      ),
    ]);
  } catch (err) {
    // Best-effort by design — see above.
    console.warn("[Push] Failed to drop subscription:", err);
    return { done: false };
  }
}
